import {
  Field,
  FieldError,
  FieldLabel,
  Label,
  MultiSelectSearch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { useSnomedSearch } from "@/network/useOntologies";
import {
  constants,
  FamilyMemberHistory,
  getCodeValue,
  getDisplay,
} from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { useGetFamilyMemberHistoryOptions } from "../network";
import { familyMemberHistoryFormSchema } from "../schemas";

interface FamilyMemberHistoryFormProps {
  familyMemberHistory?: FamilyMemberHistory;
  onSubmit: (data: z.infer<typeof familyMemberHistoryFormSchema>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CONDITION_SNOMED_PARENT_CODE = "404684003"; // Clinical finding

export const FamilyMemberHistoryForm = ({
  familyMemberHistory,
  onSubmit,
  onCancel,
  isSubmitting,
}: FamilyMemberHistoryFormProps) => {
  const { patientId } = useParams({ strict: false });
  const [searchTerm, setSearchTerm] = useState("");
  const { data: relationshipData } = useGetFamilyMemberHistoryOptions(
    patientId as string,
  );
  const { data: conditionCodes, isLoading: isLoadingConditionCodes } =
    useSnomedSearch({
      searchTerm,
      eclExpression: `<< ${CONDITION_SNOMED_PARENT_CODE}`,
    });
  const form = useForm<z.infer<typeof familyMemberHistoryFormSchema>>({
    resolver: zodResolver(familyMemberHistoryFormSchema),
    defaultValues: {
      relationshipCode: familyMemberHistory?.relationship
        ? getCodeValue(
            familyMemberHistory.relationship,
            FamilyMemberHistory.RelationshipCode.system,
          )
        : undefined,
      conditionCodes:
        familyMemberHistory?.condition?.map((condition) => {
          const isSnomed = Boolean(
            getCodeValue(condition.code, constants.SNOMED_SYSTEM_URL),
          );
          return {
            code:
              getCodeValue(condition.code, constants.SNOMED_SYSTEM_URL) ||
              getCodeValue(condition.code, constants.CURAI_KB_SYSTEM_URL),
            display: getDisplay(condition.code),
            system: isSnomed
              ? constants.SNOMED_SYSTEM_URL
              : constants.CURAI_KB_SYSTEM_URL,
          };
        }) || [],
    },
  });

  // Ensure the currently selected relationship appears in the options when editing,
  // even if the backend filters it out.
  const relationshipOptions = useMemo(() => {
    const options = relationshipData?.relationshipOptions ?? [];
    if (familyMemberHistory?.relationship) {
      const selectedCode = getCodeValue(
        familyMemberHistory.relationship,
        FamilyMemberHistory.RelationshipCode.system,
      );
      const selectedDisplay = getDisplay(familyMemberHistory.relationship);
      // Guard against adding a duplicate option in case we change the backend
      if (
        selectedCode &&
        !options.some((option) => option.code === selectedCode)
      ) {
        return [
          { code: selectedCode, display: selectedDisplay || selectedCode },
          ...options,
        ];
      }
    }
    return options;
  }, [relationshipData, familyMemberHistory]);

  return (
    <div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2 px-3 py-2"
      >
        <Label>{familyMemberHistory?.id ? "Edit" : "Add"} Family History</Label>
        <div className="flex flex-row gap-2">
          <Controller
            control={form.control}
            name="relationshipCode"
            disabled={Boolean(familyMemberHistory?.id)}
            render={({ field, fieldState }) => (
              <Field className="flex-1">
                <FieldLabel htmlFor="relationshipCode">Relationship</FieldLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={field.disabled}
                >
                  <SelectTrigger id="relationshipCode">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {relationshipOptions?.map((option) => (
                      <SelectItem key={option.code} value={option.code}>
                        {option.display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="conditionCodes"
            render={({ field, fieldState }) => (
              <Field className="flex-1">
                <FieldLabel htmlFor="conditionCodes">Conditions</FieldLabel>
                <MultiSelectSearch
                  placeholder="Search for conditions"
                  options={
                    conditionCodes?.map((code) => ({
                      label: code.display,
                      value: code.code,
                      metadata: {
                        system: code.system,
                      },
                    })) || []
                  }
                  selectedItems={field.value.map((code) => ({
                    label: code.display,
                    value: code.code,
                    metadata: {
                      system: code.system,
                    },
                  }))}
                  isLoading={isLoadingConditionCodes}
                  onInputChange={(input) => {
                    setSearchTerm(input);
                  }}
                  onItemAdded={(item) => {
                    form.setValue("conditionCodes", [
                      ...field.value,
                      {
                        code: item.value as string,
                        display: item.label,
                        system:
                          (item.metadata as { system?: string })?.system || "",
                      },
                    ]);
                  }}
                  onItemRemoved={(item) => {
                    form.setValue(
                      "conditionCodes",
                      [...field.value].filter(
                        (condition) => condition.code !== item.value,
                      ),
                    );
                  }}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </form>
    </div>
  );
};
